import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

export interface PanelContent {
	id: string;
	component: React.ReactNode;
	meta?: {
		title?: React.ReactNode;
		isDirty?: boolean;
		onClose?: () => void;
		onBack?: () => void;
		actions?: React.ReactNode;
	};
}

interface PanelStore {
	currentPanel: PanelContent | null;
	panelHistory: PanelContent[];
	maxHistorySize: number;

	setPanel: (content: PanelContent) => void;
	clearPanel: () => void;
	goBack: () => void;
	hasHistory: () => boolean;
	updatePanelMeta: (meta: Partial<PanelContent["meta"]>) => void;
}

export const usePanelStore = create<PanelStore>()(
	subscribeWithSelector((set, get) => ({
		currentPanel: null,
		panelHistory: [],
		maxHistorySize: 10,

		setPanel: (content: PanelContent) => {
			const { currentPanel, panelHistory, maxHistorySize } = get();

			if (currentPanel && currentPanel.id !== content.id) {
				const newHistory = [currentPanel, ...panelHistory].slice(
					0,
					maxHistorySize,
				);
				set({
					currentPanel: content,
					panelHistory: newHistory,
				});
			} else {
				set({ currentPanel: content });
			}
		},

		clearPanel: () => {
			set({ currentPanel: null, panelHistory: [] });
		},

		goBack: () => {
			const { panelHistory, currentPanel } = get();
			if (panelHistory.length > 0) {
				if (currentPanel?.meta?.onBack) {
					currentPanel.meta.onBack();
				}

				const [previousPanel, ...restHistory] = panelHistory;
				set({
					currentPanel: previousPanel,
					panelHistory: restHistory,
				});
			}
		},

		hasHistory: () => {
			return get().panelHistory.length > 0;
		},

		updatePanelMeta: (meta: Partial<PanelContent["meta"]>) => {
			const { currentPanel } = get();
			if (currentPanel) {
				set({
					currentPanel: {
						...currentPanel,
						meta: { ...currentPanel.meta, ...meta },
					},
				});
			}
		},
	})),
);

export const usePanelActions = () => {
	const { setPanel, clearPanel, goBack, updatePanelMeta } = usePanelStore();
	return { setPanel, clearPanel, goBack, updatePanelMeta };
};

export const useCurrentPanel = () =>
	usePanelStore((state) => state.currentPanel);

export const usePanelHistory = () =>
	usePanelStore((state) => state.panelHistory);

export const useHasHistory = () =>
	usePanelStore((state) => state.panelHistory.length > 0);
